import useSSE from '../useSSE';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function getWeekDates(offset=0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate()-(day===0?6:day-1)+offset*7);
  return Array.from({length:7},(_,i)=>{
    const d=new Date(monday); d.setDate(monday.getDate()+i); return d.toISOString().slice(0,10);
  });
}

function Avatar({name,color,size=24}) {
  const i=(name||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  return <div style={{width:size,height:size,borderRadius:'50%',background:color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.36,fontWeight:'700',color:'#fff',flexShrink:0}}>{i}</div>;
}

function pColor(p) {
  return p>=100?'#16A34A':p>=60?'#5B5BD6':p>=30?'#D97706':'#DC2626';
}

const STATUS_COLORS = {
  not_started:'#8A93A6', planning:'#2563EB', in_progress:'#5B5BD6',
  escalated:'#DC2626', at_risk:'#D97706', on_hold:'#8A93A6',
  completed:'#16A34A', cancelled:'#6B7280'
};
const STATUS_LABELS = {
  not_started:'Not Started', planning:'Planning', in_progress:'In Progress',
  escalated:'Escalated', at_risk:'At Risk', on_hold:'On Hold',
  completed:'Completed', cancelled:'Cancelled'
};

export default function TimesheetPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const backToDash = () => navigate(spaceId ? `/spaces/${spaceId}` : '/spaces');

  useSSE(spaceId, {
    task_updated:       () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
    timesheet_updated:  () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
  });

  // Silent reload when phase access changes (permission granted/revoked)
  React.useEffect(() => {
    function onAccessChanged() {
      silentRef.current = true;
      load().finally(() => { silentRef.current = false; });
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [spaceId]);
  const { user } = useAuth();
  const [tab, setTab] = useState('tasks'); // tasks | week
  const [weekOffset, setWeekOffset] = useState(0);
  const [allEntries, setAllEntries] = useState([]);
  const [taskRows, setTaskRows] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [visibleUsers, setVisibleUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const silentRef = React.useRef(false); // true during silent poll refresh

  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [saving, setSaving] = useState({});

  const days = getWeekDates(weekOffset);
  const today = new Date().toISOString().slice(0,10);
  const isAdmin = ['superadmin','admin','manager'].includes(user?.role);
  const weekLabel = `${new Date(days[0]+'T00:00:00').toLocaleDateString('en',{month:'short',day:'numeric'})} – ${new Date(days[6]+'T00:00:00').toLocaleDateString('en',{month:'short',day:'numeric',year:'numeric'})}`;

  // ── Export Professional Excel
  async function exportTimesheetCSV(rows, projectName, users) {
    rows = Array.isArray(rows) ? rows : [];
    const { downloadStyledWorkbook } = await import('../utils/excelExport');
    try {
      const dateStr = new Date().toISOString().slice(0,10);
      const safeName = (projectName||'Timesheet').replace(/[^a-z0-9]/gi,'_').slice(0,30);
      const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '';
      const r2 = n => Math.round((parseFloat(n)||0)*100)/100;

      // ── Sheet 1: Detailed Log ─────────────────────────────────────────────
      const detailCols = [
        { header:'#',                 key:'n',        width:5,  align:'center' },
        { header:'Date',              key:'date',     width:14 },
        { header:'Team Member',       key:'user',     width:26 },
        { header:'Phase',             key:'phase',    width:22 },
        { header:'List / Task Group', key:'list',     width:24 },
        { header:'Task',              key:'task',     width:34 },
        { header:'Hours',             key:'hours',    width:9,  align:'center', numFmt:'0.00' },
        { header:'Billable',          key:'billable', width:10, align:'center' },
        { header:'Notes',             key:'notes',    width:38 },
      ];
      const detailRows = rows.map((r,i)=>{
        const user = users.find(u=>u.id===r.user_id)||{full_name:r.user_id||'Unknown'};
        return {
          n: i+1,
          date: fmtD(r.date||r.week_label||''),
          user: user.full_name||'',
          phase: r.phase_name||'',
          list: r.list_name||'',
          task: r.task_name||'',
          hours: r2(r.hours),
          billable: r.is_billable ? 'Yes' : 'No',
          notes: r.notes||'',
        };
      });
      const totalHours = rows.reduce((s,r)=>s+(parseFloat(r.hours)||0),0);
      const billableHours = rows.filter(r=>r.is_billable).reduce((s,r)=>s+(parseFloat(r.hours)||0),0);

      // ── Sheet 2: Summary by Phase > List > Task ───────────────────────────
      const summaryMap = {};
      rows.forEach(r => {
        const ph = r.phase_name||'No Phase';
        const li = r.list_name||'No List';
        const tk = r.task_name||'No Task';
        const user = users.find(u=>u.id===r.user_id)||{full_name:r.user_id||'Unknown'};
        summaryMap[ph] = summaryMap[ph]||{};
        summaryMap[ph][li] = summaryMap[ph][li]||{};
        summaryMap[ph][li][tk] = summaryMap[ph][li][tk]||{hours:0,users:new Set()};
        summaryMap[ph][li][tk].hours += parseFloat(r.hours||0);
        summaryMap[ph][li][tk].users.add(user.full_name);
      });
      const summaryCols = [
        { header:'Phase / List / Task', key:'label',   width:48 },
        { header:'Total Hours',         key:'hours',   width:14, align:'center', numFmt:'0.00' },
        { header:'Team Members',        key:'members', width:42 },
      ];
      const summaryRows = [];
      let grandTotal = 0;
      Object.keys(summaryMap).sort().forEach(ph => {
        const phHours = Object.values(summaryMap[ph]).reduce((s,li)=>s+Object.values(li).reduce((a,t)=>a+t.hours,0),0);
        grandTotal += phHours;
        summaryRows.push({ label: ph, hours: r2(phHours), members:'' });
        Object.keys(summaryMap[ph]).sort().forEach(li => {
          const liHours = Object.values(summaryMap[ph][li]).reduce((a,t)=>a+t.hours,0);
          summaryRows.push({ label: '   └ '+li, hours: r2(liHours), members:'' });
          Object.keys(summaryMap[ph][li]).sort().forEach(tk => {
            const t = summaryMap[ph][li][tk];
            summaryRows.push({ label:'      · '+tk, hours:r2(t.hours), members:[...t.users].join(', ') });
          });
        });
      });

      // ── Sheet 3: By Team Member ───────────────────────────────────────────
      const byUser = {};
      rows.forEach(r => {
        const user = users.find(u=>u.id===r.user_id)||{full_name:r.user_id||'Unknown'};
        const name = user.full_name;
        byUser[name] = byUser[name]||{hours:0,billable:0,tasks:new Set(),days:new Set()};
        byUser[name].hours += parseFloat(r.hours||0);
        if (r.is_billable) byUser[name].billable += parseFloat(r.hours||0);
        byUser[name].tasks.add(r.task_name||'—');
        byUser[name].days.add(r.date||'');
      });
      const teamCols = [
        { header:'Team Member',    key:'name',     width:26 },
        { header:'Total Hours',    key:'hours',    width:14, align:'center', numFmt:'0.00' },
        { header:'Billable Hours', key:'billable', width:16, align:'center', numFmt:'0.00' },
        { header:'Billable %',     key:'pct',      width:12, align:'center' },
        { header:'Tasks Worked',   key:'tasks',    width:14, align:'center' },
        { header:'Working Days',   key:'days',     width:14, align:'center' },
      ];
      const teamRows = Object.keys(byUser).sort().map(name => {
        const u2 = byUser[name];
        return {
          name,
          hours: r2(u2.hours),
          billable: r2(u2.billable),
          pct: u2.hours>0 ? Math.round(u2.billable/u2.hours*100)+'%' : '0%',
          tasks: u2.tasks.size,
          days: u2.days.size,
        };
      });

      await downloadStyledWorkbook(`Timesheet_${safeName}_${dateStr}.xlsx`, [
        {
          name: 'Detailed Log',
          title: `Timesheet — ${projectName||''}`,
          subtitle: `Generated ${new Date().toLocaleString('en-GB')} · ${rows.length} entr${rows.length!==1?'ies':'y'}`,
          columns: detailCols,
          rows: detailRows,
          totalRow: { task:'TOTAL HOURS', hours:r2(totalHours), billable:`Billable: ${r2(billableHours)}` },
        },
        { name:'Summary', columns:summaryCols, rows:summaryRows, totalRow:{ label:'GRAND TOTAL', hours:r2(grandTotal) } },
        { name:'By Team Member', columns:teamCols, rows:teamRows },
      ]);
    } catch(e) {
      console.error('Excel export error:', e);
      const csvRows = [['Date','User','Task','Hours'].join(',')];
      rows.forEach(r => {
        const user = users.find(u=>u.id===r.user_id)||{};
        csvRows.push([r.date||'',user.full_name||'',r.task_name||'',r.hours||0].map(v=>'"'+String(v||'').replace(/"/g,'""')+'"').join(','));
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob(['\uFEFF'+csvRows.join('\n')],{type:'text/csv'}));
      a.download = `Timesheet_${projectName}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
    }
  }


  // ── Export SVG/PDF — professional printable report ────────────────────
  function exportTimesheetSVG(projectName, rows, users) {
    const esc = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const dateStr = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
    const totalHours = rows.reduce((s,r)=>s+parseFloat(r.hours||0),0);
    const totalBillable = rows.filter(r=>r.is_billable).reduce((s,r)=>s+parseFloat(r.hours||0),0);

    // Group by user for summary
    const byUser = {};
    rows.forEach(r=>{
      const user = users.find(u=>u.id===r.user_id)||{full_name:r.user_id||'—'};
      const name = user.full_name||'—';
      if(!byUser[name]) byUser[name]={hours:0,tasks:new Set()};
      byUser[name].hours += parseFloat(r.hours||0);
      byUser[name].tasks.add(r.task_name||'—');
    });

    const pad=40, rowH=28, hdrH=34;
    const cols=[
      {label:'Date',       w:90},
      {label:'User',       w:160},
      {label:'Phase',      w:130},
      {label:'List',       w:150},
      {label:'Task',       w:220},
      {label:'Hours',      w:70},
      {label:'Billable',   w:75},
      {label:'Notes',      w:180},
    ];
    const totalW = cols.reduce((s,c)=>s+c.w,0) + pad*2;
    const summaryRows = Object.keys(byUser).sort();
    const dataRows = rows.slice(0,200); // cap at 200 rows for SVG
    const totalH = pad*2 + 80 + hdrH + dataRows.length*rowH + 40 + 20 + summaryRows.length*22 + 60;

    let y = pad;
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}">`;
    svg += `<rect width="${totalW}" height="${totalH}" fill="#F8F9FC"/>`;

    // Header block
    svg += `<rect x="${pad}" y="${y}" width="${totalW-pad*2}" height="70" rx="8" fill="#1E293B"/>`;
    svg += `<text x="${pad+20}" y="${y+26}" font-family="Arial" font-size="18" font-weight="bold" fill="white">📊 Timesheet Report — ${esc(projectName)}</text>`;
    svg += `<text x="${pad+20}" y="${y+46}" font-family="Arial" font-size="11" fill="#94A3B8">Generated: ${dateStr}</text>`;
    svg += `<text x="${totalW-pad-20}" y="${y+26}" font-family="Arial" font-size="13" font-weight="bold" fill="#38BDF8" text-anchor="end">${rows.length} entries</text>`;
    svg += `<text x="${totalW-pad-20}" y="${y+46}" font-family="Arial" font-size="12" fill="#34D399" text-anchor="end">Total: ${Math.round(totalHours*100)/100}h${totalBillable>0?' | Billable: '+Math.round(totalBillable*100)/100+'h':''}</text>`;
    y += 80;

    // Column headers
    svg += `<rect x="${pad}" y="${y}" width="${totalW-pad*2}" height="${hdrH}" fill="#5B5BD6" rx="4"/>`;
    let cx = pad;
    cols.forEach(col=>{
      svg += `<text x="${cx+8}" y="${y+22}" font-family="Arial" font-size="11" font-weight="bold" fill="white">${esc(col.label)}</text>`;
      cx += col.w;
    });
    y += hdrH;

    // Data rows
    dataRows.forEach((row,i)=>{
      const bg = i%2===0?'white':'#F8FAFC';
      svg += `<rect x="${pad}" y="${y}" width="${totalW-pad*2}" height="${rowH}" fill="${bg}"/>`;
      svg += `<line x1="${pad}" y1="${y+rowH}" x2="${totalW-pad}" y2="${y+rowH}" stroke="#E2E8F0" stroke-width="1"/>`;
      const user = users.find(u=>u.id===row.user_id)||{full_name:row.user_id||'—'};
      const vals=[
        row.date||'',
        user.full_name||'',
        row.phase_name||'',
        row.list_name||'',
        row.task_name||'',
        Math.round(parseFloat(row.hours||0)*100)/100+'h',
        row.is_billable?'✓':'',
        row.notes||''
      ];
      cx = pad;
      vals.forEach((v,ci)=>{
        const maxW = cols[ci].w - 14;
        const txt = String(v).length > maxW/6.5 ? String(v).slice(0,Math.floor(maxW/6.5))+'…' : String(v);
        const color = ci===5?'#5B5BD6':ci===6?'#16A34A':'#1E293B';
        svg += `<text x="${cx+7}" y="${y+18}" font-family="Arial" font-size="10" fill="${color}">${esc(txt)}</text>`;
        cx += cols[ci].w;
      });
      y += rowH;
    });
    if(rows.length > 200) {
      svg += `<text x="${pad+10}" y="${y+18}" font-family="Arial" font-size="10" fill="#94A3B8" font-style="italic">… and ${rows.length-200} more rows (download Excel for full data)</text>`;
      y += rowH;
    }
    y += 20;

    // Summary by user
    svg += `<text x="${pad}" y="${y+14}" font-family="Arial" font-size="12" font-weight="bold" fill="#1E293B">Hours by User</text>`;
    y += 22;
    summaryRows.forEach(name=>{
      const hrs = Math.round(byUser[name].hours*100)/100;
      const tasks = byUser[name].tasks.size;
      svg += `<text x="${pad+10}" y="${y+14}" font-family="Arial" font-size="10" fill="#374151">• ${esc(name)}</text>`;
      svg += `<text x="${pad+200}" y="${y+14}" font-family="Arial" font-size="10" font-weight="bold" fill="#5B5BD6">${hrs}h</text>`;
      svg += `<text x="${pad+270}" y="${y+14}" font-family="Arial" font-size="10" fill="#94A3B8">${tasks} task${tasks!==1?'s':''}</text>`;
      y += 22;
    });

    // Footer
    y += 14;
    svg += `<line x1="${pad}" y1="${y}" x2="${totalW-pad}" y2="${y}" stroke="#E2E8F0" stroke-width="1"/>`;
    svg += `<text x="${totalW/2}" y="${y+18}" font-family="Arial" font-size="10" fill="#94A3B8" text-anchor="middle">Space Communication Technologies — Confidential</text>`;

    svg += '</svg>';

    // Open as printable HTML page (better than SVG download for print-to-PDF)
    const html = `<!DOCTYPE html><html><head><title>Timesheet — ${esc(projectName)}</title>
      <style>body{margin:0;padding:0;background:#F8F9FC}@media print{@page{size:A3 landscape;margin:10mm}}</style>
      </head><body>
      <div style="text-align:right;padding:10px 20px;display:flex;justify-content:flex-end;gap:8px">
        <button onclick="window.print()" style="padding:8px 20px;background:#5B5BD6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700">🖨️ Print / Save as PDF</button>
        <button onclick="window.close()" style="padding:8px 14px;background:#E2E8F0;border:none;border-radius:6px;cursor:pointer;font-size:13px">✕ Close</button>
      </div>
      ${svg}
      </body></html>`;
    const w = window.open('','_blank','width=1200,height=800');
    if(w){ w.document.write(html); w.document.close(); }
  }

  useEffect(()=>{ load(); },[weekOffset,spaceId]);
  // ── Silent background refresh ─────────────────────────────────────────────
  useEffect(()=>{
    let lastTs=null, active=true, fails=0;
    const poll=async()=>{
      if(!active||!spaceId) return;
      try {
        const r=await api.get(`/spaces/${spaceId}/last-updated`).catch(()=>null);
        if(!r){fails++;if(fails>4)active=false;return;}
        fails=0;
        if(lastTs===null){lastTs=r.last_updated;return;}
        if(r.last_updated!==lastTs){lastTs=r.last_updated;load();}
      } catch(_){fails++;}
    };
    const iv=setInterval(poll,15000);
    return()=>{active=false;clearInterval(iv);};
  },[spaceId]);

  async function load() {
    if(!silentRef.current) setLoading(true);
    try {
      const [spaceData, bulkData, entries] = await Promise.all([
        api.get(`/spaces/${spaceId}`).catch(()=>null),
        api.get(`/spaces/${spaceId}/timesheet-bulk`).catch(()=>({tasks:[],members:[]})),
        api.get(`/timesheet?project_id=${spaceId}&start=${days[0]}&end=${days[6]}`).catch(()=>[]),
      ]);
      setSpace(spaceData);

      // Build allUsers: project members UNION users who have timesheet entries
      const members = bulkData?.members || [];
      const entriesArr = Array.isArray(entries) ? entries : [];
      
      // Add any user who has logged hours but isn't in project_members list
      const memberIds = new Set(members.map(m=>m.id));
      const extraUsers = [];
      entriesArr.forEach(e=>{
        if (!memberIds.has(e.user_id) && e.user_id && e.full_name) {
          memberIds.add(e.user_id);
          extraUsers.push({ id: e.user_id, full_name: e.full_name, avatar_color: e.avatar_color||'#5B5BD6' });
        }
      });
      const allMembers = [...members, ...extraUsers];
      setAllUsers(allMembers);
      
      // Show ALL members in week view by default (not just a subset)
      setVisibleUsers(allMembers.map(u=>u.id));

      setAllEntries(entriesArr);

      // Build task rows from bulk data (FIX 4 — single API call)
      const rows = (bulkData?.tasks || []).map(t => ({
        id:t.id, name:t.name, status:t.status, task_type:t.task_type,
        estimated: t.time_budget||0,
        loggedHours: t.loggedHours||0,
        remaining: Math.max(0,(t.time_budget||0)-(t.loggedHours||0)),
        progress: t.progress||0,
        assignees: t.assignees||[],
        pms: Array.isArray(t.project_managers)?t.project_managers:[],
        phase: t.phase_name||'', milestone: t.list_name||'',
        isSubtask:false, parent:null,
      }));
      setTaskRows(rows);
    } catch(e) { console.error('Timesheet load error:', e); }
    setLoading(false);
  }

  // Week view helpers
  async function setHours(userId, date, value) {
    const h = parseFloat(value)||0;
    const key=`${userId}_${date}`;
    setSaving(p=>({...p,[key]:true}));
    try {
      await api.post('/timesheet',{date,hours:h,user_id:userId});
      // refresh entries only
      const updated = await api.get(
        `/timesheet?project_id=${spaceId}&start=${days[0]}&end=${days[6]}`
      ).catch(()=>[]);
      setAllEntries(Array.isArray(updated)?updated:[]);
    } catch(e){}
    setSaving(p=>({...p,[key]:false}));
  }
  function getHours(uid,date) { return allEntries.find(e=>e.user_id===uid&&e.date===date)?.hours||0; }
  function getUserWeekTotal(uid) { return days.reduce((s,d)=>s+getHours(uid,d),0); }
  function getDayTotal(date) {
    const uids = displayUsers.map(u=>u.id);
    return uids.reduce((s,uid)=>s+getHours(uid,date),0);
  }

  // Filters for task view
  const filteredTaskRows = taskRows.filter(r=>{
    if (filterStatus!=='all' && r.status!==filterStatus) return false;
    if (filterUser!=='all') {
      if (!r.assignees.find(a=>a.id===filterUser)) return false;
    }
    return true;
  });

  const displayUsers = allUsers.filter(u=>visibleUsers.includes(u.id));
  const COL = `200px ${days.map(()=>'1fr').join(' ')} 80px`;

  // Summary stats
  const totalEstimated = filteredTaskRows.filter(r=>!r.isSubtask).reduce((s,r)=>s+r.estimated,0);
  const totalLogged = filteredTaskRows.filter(r=>!r.isSubtask).reduce((s,r)=>s+r.loggedHours,0);
  const totalRemaining = Math.max(0,totalEstimated-totalLogged);
  const burnPct = totalEstimated>0?Math.min(100,(totalLogged/totalEstimated)*100):0;
  const burnColor = burnPct>=90?'#DC2626':burnPct>=70?'#D97706':'#16A34A';

  return (
    <div className="page-enter" style={{flex:1,display:'flex',flexDirection:'column',overflowY:'auto',overflowX:'hidden',background:'var(--bg)',minHeight:0}}>


      {/* Header */}
      <div style={{padding:'14px 24px',borderBottom:'1px solid var(--border)',background:'var(--surface)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:'16px'}}>
          <button onClick={backToDash} className="btn btn-sm"
            style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'11px',padding:'4px 10px',borderRadius:'12px',fontWeight:'600',color:'var(--muted)',flexShrink:0,whiteSpace:'nowrap'}}>
            ← Overview
          </button>
          <div>
            <h1 style={{fontSize:'17px',fontWeight:'700',color:'var(--text)',margin:0}}>
              Timesheet
              {space&&<span style={{color:'var(--muted)',fontWeight:'400',fontSize:'14px'}}> — {space.name}</span>}
            </h1>
            <p style={{fontSize:'11px',color:'var(--muted)',margin:'2px 0 0'}}>Track time estimates, logged hours and task progress</p>
          </div>
          <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'8px'}}>
            {/* Tab toggle */}
            <div style={{display:'flex',gap:'2px',background:'var(--surface2)',borderRadius:'var(--radius)',padding:'3px',border:'1px solid var(--border)'}}>
              {[['tasks','📋 By Task'],['week','📅 By Week']].map(([v,l])=>(
                <button key={v} onClick={()=>setTab(v)}
                  className={`btn btn-sm ${tab===v?'btn-primary':''}`}
                  style={{padding:'4px 12px',fontSize:'12px'}}>{l}</button>
              ))}
            </div>
            {tab==='week'&&<>
              <button className="btn btn-sm" onClick={()=>setWeekOffset(w=>w-1)}>‹</button>
              <span style={{fontSize:'13px',fontWeight:'600',minWidth:'190px',textAlign:'center'}}>{weekLabel}</span>
              <button className="btn btn-sm" onClick={()=>setWeekOffset(w=>w+1)}>›</button>
              <button className="btn btn-sm" onClick={()=>setWeekOffset(0)}>Today</button>
              {isAdmin&&<button className="btn btn-sm btn-primary" onClick={()=>setShowAddUser(true)}>+ Member</button>}
            </>}
            {/* Export buttons */}
            <button className="btn btn-sm" onClick={async()=>{
                // Issue 4 fix: fetch ALL entries for the project (not just the
                // visible week) so the export contains every logged hour.
                let full = [];
                try { full = await api.get(`/timesheet?project_id=${spaceId}`); } catch(_){ full = allEntries; }
                full = Array.isArray(full) ? full : [];
                const taskMap = {};
                taskRows.forEach(t=>{ taskMap[t.id]={phase:t.phase,list:t.milestone,name:t.name}; });
                const enriched = full.map(e=>({
                  ...e,
                  task_name: e.task_name || taskMap[e.task_id]?.name || '—',
                  phase_name: e.phase_name || taskMap[e.task_id]?.phase || '',
                  list_name: e.list_name || taskMap[e.task_id]?.list || '',
                  user_id: e.user_id,
                }));
                exportTimesheetCSV(enriched, space?.name||'Timesheet', allUsers);
              }}
              title="Export as professional Excel workbook">
              📊 Export Excel
            </button>
            <button className="btn btn-sm" onClick={async()=>{
                let full2 = [];
                try { full2 = await api.get(`/timesheet?project_id=${spaceId}`); } catch(_){ full2 = allEntries; }
                full2 = Array.isArray(full2) ? full2 : [];
                const taskMap2 = {};
                taskRows.forEach(t=>{ taskMap2[t.id]={phase:t.phase,list:t.milestone,name:t.name}; });
                const enriched2 = full2.map(e=>({
                  ...e,
                  task_name: e.task_name||taskMap2[e.task_id]?.name||'—',
                  phase_name: e.phase_name||taskMap2[e.task_id]?.phase||'',
                  list_name: e.list_name||taskMap2[e.task_id]?.list||'',
                }));
                exportTimesheetSVG(space?.name||'Timesheet', enriched2, allUsers);
              }}
              title="Export as printable PDF report">
              📄 Export SVG/PDF
            </button>
          </div>
        </div>
      </div>

      <div id="timesheet-container" style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:'16px'}}>

        {/* ─── BY TASK VIEW ─── */}
        {tab==='tasks' && (
          <>
            {/* Summary cards */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px'}}>
              {[
                {label:'Total Budgeted',value:`${totalEstimated.toFixed(1)}h`,sub:'planned hours',color:'#5B5BD6',icon:'📊'},
                {label:'Hours Logged',value:`${totalLogged.toFixed(1)}h`,sub:`${Math.round(burnPct)}% of budget`,color:burnColor,icon:'⏱'},
                {label:'Hours Remaining',value:`${totalRemaining.toFixed(1)}h`,sub:'to complete',color:'#D97706',icon:'⏳'},
                {label:'Tasks with Estimates',value:filteredTaskRows.filter(r=>!r.isSubtask&&r.estimated>0).length,sub:`of ${filteredTaskRows.filter(r=>!r.isSubtask).length} total tasks`,color:'#16A34A',icon:'✅'},
              ].map((c,i)=>(
                <div key={i} className="card" style={{padding:'16px 18px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'8px'}}>
                    <span style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)'}}>{c.label}</span>
                    <span style={{fontSize:'16px'}}>{c.icon}</span>
                  </div>
                  <div style={{fontSize:'26px',fontWeight:'800',color:c.color,letterSpacing:'-1px',lineHeight:1}}>{c.value}</div>
                  <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'4px'}}>{c.sub}</div>
                </div>
              ))}
            </div>

            {/* Budget burn bar */}
            {totalEstimated>0&&(
              <div className="card" style={{padding:'16px 20px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                  <div style={{fontSize:'12px',fontWeight:'700',color:'var(--text)'}}>Time Budget Utilization</div>
                  <div style={{fontSize:'12px',color:'var(--muted)'}}>
                    <span style={{color:burnColor,fontWeight:'700'}}>{totalLogged.toFixed(1)}h</span> logged of <strong>{totalEstimated.toFixed(1)}h</strong> budgeted
                  </div>
                </div>
                <div style={{height:12,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden',position:'relative'}}>
                  <div style={{height:'100%',width:`${burnPct}%`,background:burnColor,borderRadius:'99px',transition:'width .5s ease'}}/>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'10px',color:'var(--muted)',marginTop:'6px'}}>
                  <span>0h</span>
                  <span style={{color:burnColor,fontWeight:'600'}}>{Math.round(burnPct)}% consumed</span>
                  <span>{totalEstimated.toFixed(1)}h</span>
                </div>
              </div>
            )}

            {/* Filters */}
            <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
              <span style={{fontSize:'12px',color:'var(--muted)',fontWeight:'600'}}>Filter:</span>
              <select className="select" value={filterUser} onChange={e=>setFilterUser(e.target.value)} style={{width:'160px',height:'32px',padding:'4px 28px 4px 10px',fontSize:'12px'}}>
                <option value="all">All Assignees</option>
                {allUsers.map(u=><option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
              <select className="select" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{width:'150px',height:'32px',padding:'4px 28px 4px 10px',fontSize:'12px'}}>
                <option value="all">All Statuses</option>
                {Object.entries(STATUS_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
              <span style={{fontSize:'11px',color:'var(--muted)',marginLeft:'4px'}}>{filteredTaskRows.length} rows</span>
            </div>

            {/* Task table */}
            <div className="card" style={{overflow:'hidden'}}>
              {/* Header */}
              <div style={{display:'grid',gridTemplateColumns:'28px minmax(200px,2fr) 110px 90px 120px 80px 80px 80px 1fr',background:'var(--surface2)',borderBottom:'2px solid var(--border)'}}>
                {['','Task','Phase / Milestone','Status','Assignees','Budget H','Logged','Remaining','Progress'].map((h,i)=>(
                  <div key={i} style={{padding:'9px 12px',fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted)'}}>{h}</div>
                ))}
              </div>

              {loading&&<div style={{padding:'40px',textAlign:'center',color:'var(--muted)',fontSize:'13px'}}>Loading timesheet data…</div>}

              {!loading&&filteredTaskRows.length===0&&(
                <div style={{padding:'48px',textAlign:'center',color:'var(--muted)'}}>
                  <div style={{fontSize:'32px',marginBottom:'12px'}}>⏱</div>
                  <div style={{fontSize:'14px',fontWeight:'600',color:'var(--text)',marginBottom:'6px'}}>No tasks with time estimates</div>
                  <div style={{fontSize:'12px'}}>Add time estimates to your tasks to track hours here</div>
                </div>
              )}

              {!loading&&filteredTaskRows.map((r,i)=>{
                const pc = pColor(r.progress);
                const sc = STATUS_COLORS[r.status]||'#8A93A6';
                return (
                  <div key={r.id} style={{display:'grid',gridTemplateColumns:'28px minmax(200px,2fr) 110px 90px 120px 80px 80px 80px 1fr',borderBottom:'1px solid var(--border)',background:r.isSubtask?'rgba(91,91,214,0.025)':'transparent',transition:'background .1s'}}
                    onMouseEnter={e=>e.currentTarget.style.background=r.isSubtask?'rgba(91,91,214,0.06)':'var(--surface2)'}
                    onMouseLeave={e=>e.currentTarget.style.background=r.isSubtask?'rgba(91,91,214,0.025)':'transparent'}>
                    {/* Type indicator */}
                    <div style={{padding:'10px 6px',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      {r.isSubtask
                        ? <span style={{color:'rgba(91,91,214,0.4)',fontSize:'11px'}}>└</span>
                        : <span style={{fontSize:'12px'}}>{r.task_type==='milestone'?'⭐':r.task_type==='meeting'?'📅':'📋'}</span>}
                    </div>
                    {/* Task name */}
                    <div style={{padding:'10px 12px',display:'flex',flexDirection:'column',gap:'2px'}}>
                      <span style={{fontSize:r.isSubtask?'12px':'13px',fontWeight:r.isSubtask?'400':'600',color:'var(--text)',paddingLeft:r.isSubtask?'12px':0}}>{r.name}</span>
                      {r.isSubtask&&<span style={{fontSize:'10px',color:'var(--muted)',paddingLeft:'12px'}}>↳ {r.parent}</span>}
                    </div>
                    {/* Phase / Milestone */}
                    <div style={{padding:'10px 12px',display:'flex',flexDirection:'column',gap:'2px',justifyContent:'center'}}>
                      <span style={{fontSize:'11px',color:'var(--muted2)',fontWeight:'500'}}>{r.phase}</span>
                      <span style={{fontSize:'10px',color:'var(--muted)'}}>{r.milestone}</span>
                    </div>
                    {/* Status */}
                    <div style={{padding:'10px 12px',display:'flex',alignItems:'center'}}>
                      <span style={{fontSize:'10px',fontWeight:'600',padding:'2px 8px',borderRadius:'99px',background:`${sc}18`,color:sc,border:`1px solid ${sc}30`,whiteSpace:'nowrap'}}>
                        {STATUS_LABELS[r.status]||r.status}
                      </span>
                    </div>
                    {/* Assignees */}
                    <div style={{padding:'10px 12px',display:'flex',alignItems:'center',gap:'-4px'}}>
                      {r.assignees.slice(0,4).map((a,ai)=>(
                        <div key={a.id} title={a.full_name} style={{marginLeft:ai>0?-6:0}}>
                          <Avatar name={a.full_name} color={a.avatar_color} size={22}/>
                        </div>
                      ))}
                      {r.assignees.length===0&&<span style={{fontSize:'11px',color:'var(--muted)'}}>—</span>}
                    </div>
                    {/* Budget H */}
                    <div style={{padding:'10px 12px',display:'flex',alignItems:'center'}}>
                      <span style={{fontSize:'12px',fontWeight:'600',color:r.estimated>0?'var(--text)':'var(--muted)'}}>{r.estimated>0?`${r.estimated}h`:'—'}</span>
                    </div>
                    {/* Logged */}
                    <div style={{padding:'10px 12px',display:'flex',alignItems:'center'}}>
                      <span style={{fontSize:'12px',fontWeight:'700',color:r.estimated>0?burnColor:'var(--muted)'}}>{r.loggedHours>0?`${r.loggedHours.toFixed(1)}h`:'—'}</span>
                    </div>
                    {/* Remaining */}
                    <div style={{padding:'10px 12px',display:'flex',alignItems:'center'}}>
                      <span style={{fontSize:'12px',fontWeight:'600',color:r.remaining>0?'#D97706':'#16A34A'}}>{r.estimated>0?(r.remaining>0?`${r.remaining.toFixed(1)}h`:'Done'):'—'}</span>
                    </div>
                    {/* Progress bar */}
                    <div style={{padding:'10px 12px',display:'flex',alignItems:'center',gap:'8px'}}>
                      <div style={{flex:1,height:7,background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${r.progress}%`,background:pc,borderRadius:'99px',transition:'width .4s ease'}}/>
                      </div>
                      <span style={{fontSize:'11px',fontWeight:'700',color:pc,minWidth:'32px',textAlign:'right'}}>{Math.round(r.progress)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ─── BY WEEK VIEW ─── */}
        {tab==='week' && (
          <div className="card" style={{overflow:'hidden',minWidth:'700px'}}>
            {/* Column headers */}
            <div style={{display:'grid',gridTemplateColumns:COL,background:'var(--surface2)',borderBottom:'2px solid var(--border)'}}>
              <div style={{padding:'10px 16px',fontSize:'11px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.7px'}}>Member</div>
              {days.map((d,i)=>(
                <div key={d} style={{padding:'10px 6px',fontSize:'11px',fontWeight:'700',color:d===today?'var(--brand)':'var(--muted)',textTransform:'uppercase',letterSpacing:'.6px',textAlign:'center',background:d===today?'var(--brand-dim)':'transparent',borderLeft:'1px solid var(--border)'}}>
                  {DAY_NAMES[i]}<br/>
                  <span style={{fontSize:'10px',fontWeight:'400',opacity:.7}}>{new Date(d+'T00:00:00').getDate()}</span>
                </div>
              ))}
              <div style={{padding:'10px 6px',fontSize:'11px',fontWeight:'700',color:'var(--brand)',textAlign:'center',borderLeft:'1px solid var(--border)',background:'var(--brand-dim)'}}>Total</div>
            </div>

            {displayUsers.length===0
              ? <div style={{padding:'48px',textAlign:'center',color:'var(--muted)',fontSize:'13px'}}>
                  No members to display. <button className="btn btn-sm btn-primary" style={{marginLeft:'8px'}} onClick={()=>setShowAddUser(true)}>+ Add Member to View</button>
                </div>
              : displayUsers.map(u=>{
                const weekTotal = getUserWeekTotal(u.id);
                return (
                  <div key={u.id} style={{display:'grid',gridTemplateColumns:COL,borderBottom:'1px solid var(--border)'}}>
                    <div style={{padding:'8px 16px',display:'flex',alignItems:'center',gap:'8px',borderRight:'1px solid var(--border)'}}>
                      <Avatar name={u.full_name} color={u.avatar_color} size={26}/>
                      <div>
                        <div style={{fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>{u.full_name}</div>
                        <div style={{fontSize:'10px',color:'var(--muted)'}}>{u.role}</div>
                      </div>
                    </div>
                    {days.map(date=>{
                      const h=getHours(u.id,date);
                      const canEdit=u.id===user.id||isAdmin;
                      const key=`${u.id}_${date}`;
                      return (
                        <div key={date} style={{padding:'5px',borderLeft:'1px solid var(--border)',background:date===today?'rgba(91,91,214,0.03)':'transparent'}}>
                          <input type="number" min="0" max="24" step="0.5"
                            value={h||''} placeholder="—" disabled={!canEdit}
                            onChange={e=>canEdit&&setHours(u.id,date,e.target.value)}
                            style={{
                              width:'100%',height:'34px',
                              border:`1.5px solid ${h>8?'#DC2626':h>0?'#5B5BD6':'var(--border)'}`,
                              borderRadius:'var(--radius)',
                              background:h>8?'rgba(220,38,38,0.07)':h>0?'rgba(91,91,214,0.07)':'var(--surface2)',
                              color:h>8?'#DC2626':h>0?'#5B5BD6':'var(--muted)',
                              textAlign:'center',fontSize:'13px',fontWeight:'700',
                              fontFamily:'var(--font)',cursor:canEdit?'pointer':'not-allowed',
                              outline:'none',transition:'all .12s',
                              opacity:saving[key]?.6:1,
                            }}
                          />
                        </div>
                      );
                    })}
                    <div style={{padding:'8px 6px',display:'flex',alignItems:'center',justifyContent:'center',borderLeft:'1px solid var(--border)',background:'var(--brand-dim)',fontSize:'13px',fontWeight:'800',color:'var(--brand)'}}>
                      {weekTotal.toFixed(1).replace(/\.0$/,'')}h
                    </div>
                  </div>
                );
              })
            }

            {displayUsers.length>0&&(
              <div style={{display:'grid',gridTemplateColumns:COL,background:'var(--surface2)',borderTop:'2px solid var(--border)'}}>
                <div style={{padding:'10px 16px',fontSize:'11px',fontWeight:'700',color:'var(--muted)'}}>Day Totals</div>
                {days.map(d=>{
                  const t=getDayTotal(d);
                  return <div key={d} style={{padding:'10px 6px',textAlign:'center',fontSize:'12px',fontWeight:'700',color:t>0?'var(--text)':'var(--muted)',borderLeft:'1px solid var(--border)'}}>{t>0?`${t.toFixed(1).replace(/\.0$/,'')}h`:'—'}</div>;
                })}
                <div style={{padding:'10px 6px',textAlign:'center',fontSize:'13px',fontWeight:'800',color:'var(--brand)',borderLeft:'1px solid var(--border)',background:'var(--brand-dim)'}}>
                  {days.reduce((s,d)=>s+getDayTotal(d),0).toFixed(1).replace(/\.0$/,'')}h
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showAddUser&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'360px'}}>
            <h3 style={{fontSize:'15px',fontWeight:'700',marginBottom:'16px'}}>Add Member to View</h3>
            <select className="select" value={addUserId} onChange={e=>setAddUserId(e.target.value)}>
              <option value="">Select user…</option>
              {allUsers.filter(u=>!visibleUsers.includes(u.id)).map(u=>(<option key={u.id} value={u.id}>{u.full_name}</option>))}
            </select>
            <div style={{display:'flex',gap:'10px',justifyContent:'flex-end',marginTop:'16px'}}>
              <button className="btn" onClick={()=>setShowAddUser(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>{if(addUserId){setVisibleUsers(v=>[...v,addUserId]);setAddUserId('');setShowAddUser(false);}}}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
